import { FUNNEL_COLUMNS, type LeadStageKey } from '@crm/shared';
import { Card, CardContent, Input, Label, Spinner } from '@crm/ui';
import { ChartNoAxesColumn } from 'lucide-react';
import { useState } from 'react';
import { PageHeader } from '@/components';
import { useDocumentTitle } from '@/hooks';
import { useFunnel, useMonthlyFunnel } from '../api';
import { FunnelChart } from '../components/funnel-chart';

const porcentagem = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1 });

function inicioDoMes() {
  const data = new Date();

  return new Date(data.getFullYear(), data.getMonth(), 1).toISOString().slice(0, 10);
}

/** Conversao do funil no periodo, com a serie mes a mes. */
export function ConversionReportPage() {
  useDocumentTitle('Relatorio de conversao');

  const [de, setDe] = useState(inicioDoMes());
  const [ate, setAte] = useState(new Date().toISOString().slice(0, 10));
  const [inicio, setInicio] = useState<LeadStageKey>('LEAD');

  const from = new Date(`${de}T00:00:00`).toISOString();
  const to = new Date(`${ate}T23:59:59`).toISOString();

  const { data: funil, isLoading } = useFunnel({ from, to, startStage: inicio });
  const { data: meses } = useMonthlyFunnel({ from, to });

  const linhas = funil?.data ?? [];
  const entrada = linhas.find((linha) => linha.stage === inicio)?.total ?? 0;
  const agendou = linhas.find((linha) => linha.stage === 'SCHEDULED')?.total ?? 0;
  const pontaAPonta = entrada > 0 ? (agendou / entrada) * 100 : 0;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Relatorio de conversao"
        description="Quantos contatos entraram em cada etapa e quantos viraram horario."
        icon={<ChartNoAxesColumn />}
      />

      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <Label>De</Label>
          <Input type="date" value={de} onChange={(event) => setDe(event.target.value)} />
        </div>
        <div className="space-y-1">
          <Label>Ate</Label>
          <Input type="date" value={ate} onChange={(event) => setAte(event.target.value)} />
        </div>
      </div>

      <Card>
        <CardContent className="space-y-4 pt-6">
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Spinner />
            </div>
          ) : (
            <>
              <FunnelChart rows={linhas} inicio={inicio} onInicioChange={setInicio} />
              <p className="text-sm text-muted-foreground">
                Conversao ponta a ponta:{' '}
                <strong className="text-foreground">{porcentagem.format(pontaAPonta)}%</strong> —{' '}
                {agendou} de {entrada} leads marcaram horario. Cada numero conta leads, nao
                mensagens.
              </p>
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          <h2 className="mb-3 font-medium">Mes a mes</h2>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-muted-foreground">
                <th className="py-2 font-medium">Mes</th>
                <th className="py-2 text-right font-medium">Entraram</th>
                <th className="py-2 text-right font-medium">Agendaram</th>
                <th className="py-2 text-right font-medium">Conversao</th>
              </tr>
            </thead>
            <tbody>
              {(meses?.data ?? []).map((linha) => (
                <tr key={linha.month} className="border-b border-border last:border-0">
                  <td className="py-2">{linha.month}</td>
                  <td className="py-2 text-right">{linha.entered}</td>
                  <td className="py-2 text-right">{linha.scheduled}</td>
                  <td className="py-2 text-right">
                    {linha.entered > 0
                      ? `${porcentagem.format((linha.scheduled / linha.entered) * 100)}%`
                      : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {(meses?.data ?? []).length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              Nenhum movimento no periodo.
            </p>
          ) : null}
        </CardContent>
      </Card>

      <p className="text-xs text-muted-foreground">
        As etapas seguem o quadro: {FUNNEL_COLUMNS.map((coluna) => coluna.label).join(' › ')}.
      </p>
    </div>
  );
}
